import {
	getCapabilitySpecOrThrow,
	listModelsFromCatalog,
	type RunwareModelCapabilitySpec,
	validateToolInputAgainstModelSpec,
} from "./runware-catalog";

export type AudioModelId = string;

export type AudioModelDefinition = {
	defaultDurationSeconds: number;
	description: string;
	id: AudioModelId;
	isDefault: boolean;
	label: string;
	pricing: {
		type: "per-minute";
		usdPerMinute: number;
	};
	providerModelId: string;
	spec: RunwareModelCapabilitySpec;
	supportedDurations: {
		maxSeconds: number;
		minSeconds: number;
	};
};

function toAudioModelDefinition(model: ReturnType<typeof listModelsFromCatalog>[number]) {
	if (model.type !== "audio") return null;
	if (!model.defaultDurationSeconds || !model.supportedDurations) {
		throw new Error(`Audio model ${model.id} is missing verified duration data.`);
	}
	const spec = getCapabilitySpecOrThrow(model.id, "audio");
	return {
		defaultDurationSeconds: model.defaultDurationSeconds,
		description: model.description,
		id: model.id,
		isDefault: model.isDefault,
		label: model.label,
		pricing: model.pricing as AudioModelDefinition["pricing"],
		providerModelId: model.providerModelId,
		spec,
		supportedDurations: model.supportedDurations,
	} satisfies AudioModelDefinition;
}

export const AUDIO_MODELS = listModelsFromCatalog("audio")
	.map(toAudioModelDefinition)
	.filter((model): model is AudioModelDefinition => Boolean(model));

export function getDefaultAudioModel() {
	return AUDIO_MODELS.find((model) => model.isDefault) ?? AUDIO_MODELS[0];
}

export function getAudioModel(modelId?: string | null) {
	if (!modelId) return getDefaultAudioModel();
	return AUDIO_MODELS.find((model) => model.id === modelId) ?? null;
}

export function getAudioModelOrThrow(modelId?: string | null) {
	const model = getAudioModel(modelId);
	if (!model) {
		throw new Error("Unsupported audio model.");
	}
	return model;
}

export function assertAudioDurationSupported(
	model: AudioModelDefinition,
	durationSeconds: number,
) {
	validateToolInputAgainstModelSpec("generate_audio", model.spec, {
		durationSeconds,
	});
}

export function listAudioModels() {
	return AUDIO_MODELS.map((model) => ({
		defaultDurationSeconds: model.defaultDurationSeconds,
		description: model.description,
		id: model.id,
		isDefault: model.isDefault,
		label: model.label,
		pricing: model.pricing,
		supportedDurations: model.supportedDurations,
		type: "audio" as const,
	}));
}
